import { FC, useState } from 'react'
import { ReactComponent as LightbulbIcon } from 'src/assets/svg/lightbulb-line-24.svg'
import { TruncatedTooltip } from 'src/components/TruncatedTooltip'
import { cn } from 'src/utils/cn'
import styles from './campaignSentimentSplit.module.scss'
import { SentimentCampaign } from '../interfaces'

interface CampaignSentimentSplitProps {
  campaigns: SentimentCampaign[]
  insight: string
}

const COPY = {
  // Two empty stacks either side of a column of "0 vs 0" is not a split; it is
  // a chart of nothing, drawn as though it were an answer.
  empty:
    'No mentions were collected in this period, so there is no sentiment to split.',
  higher: 'Higher than comparison',
  lower: 'Lower than comparison',
  subtitle: 'Share of positive, neutral and negative mentions.',
  title: 'Sentiment Split',
  totalMentions: 'Total Mentions',
}

// The plot's own height, which is what a band's share of the axis turns into
// pixels (campaignSentimentSplit.module.scss, .chart).
const PLOT_HEIGHT = 320

// What a band needs to hold its labels without them colliding with the band
// above. Below the first it carries neither, below the second only its share:
// a 1% band is 3px tall, and two lines of text in it landed on its neighbours.
const SHARE_FITS = 18
const VALUE_FITS = 34

// Read bottom to top, the way the bars stack: positive at the baseline, negative
// on top. The rows between the two bars are listed in the same order as the mock,
// which reads downwards.
const BANDS = [
  { key: 'negative', label: 'Negative' },
  { key: 'neutral', label: 'Neutral' },
  { key: 'positive', label: 'Positive' },
] as const

type BandKey = (typeof BANDS)[number]['key']

// How one band moved between the two campaigns, or null when it did not.
interface BandMove {
  label: string
  leftIsHigher: boolean
}

// The axis both bars are drawn against, so a band of 900 is the same height in
// either column.
// Its top comes from the data, not from a constant: the mock's 4000 drew a real
// campaign of eleven mentions as a line on the floor.
const AXIS_STEPS = 5

// A round number at or above the tallest stack, so the ticks read 0, 4, 8… and
// not 0, 2.2, 4.4. One significant figure on the step is enough for an axis.
const axisMax = (tallest: number) => {
  if (tallest <= 0) {
    return AXIS_STEPS
  }

  const rough = tallest / AXIS_STEPS
  const magnitude = 10 ** Math.floor(Math.log10(rough))

  return Math.ceil(rough / magnitude) * magnitude * AXIS_STEPS
}

const axisTicks = (max: number) =>
  Array.from({ length: AXIS_STEPS + 1 }, (_, index) =>
    Math.round((max / AXIS_STEPS) * (AXIS_STEPS - index))
  )

const totalOf = (campaign: SentimentCampaign) =>
  BANDS.reduce((sum, { key }) => sum + campaign[key], 0)

// A campaign with no mentions has no shares, not NaN ones.
const shareOf = (campaign: SentimentCampaign, key: BandKey) => {
  const total = totalOf(campaign)

  return total ? Math.round((campaign[key] / total) * 100) : 0
}

// Unlike the campaign page's two periods, a campaign comparison states the move
// between the two in the middle of the chart: each band's counts, and a card on
// each side saying how that campaign stands against the other.
export const CampaignSentimentSplit: FC<CampaignSentimentSplitProps> = ({
  campaigns,
  insight,
}) => {
  const [hovered, setHovered] = useState<number | null>(null)
  const [left, right] = campaigns

  // Both stacks are measured against one axis, so the taller campaign sets its
  // top.
  const axisTop = axisMax(Math.max(...campaigns.map(totalOf)))
  const ticks = axisTicks(axisTop)

  // Neither campaign collected anything, so every band on both sides is zero.
  const isEmpty = campaigns.every((campaign) => totalOf(campaign) === 0)

  // One magnitude per band, mirrored on both sides: the two cards state the same
  // move from either campaign's point of view, so they cannot disagree. The
  // first campaign is the baseline, as it is everywhere else in the report.
  //
  // Null when there is nothing to state, and the row shows no cards at all:
  //   - the two campaigns are level, so neither is higher than the other. This
  //     is what a period with no mentions looks like — 0 against 0 was being
  //     reported as "↑100% Higher" against "↓100% Lower", on both sides of
  //     every band, in a report whose own insight said no mentions were
  //     collected.
  //   - the baseline is zero and the other is not, which is not a percentage of
  //     anything: growth from nothing is 'New', as it is in every other pill in
  //     the report.
  const bandMove = (key: BandKey) => {
    const baseline = left[key]
    const other = right[key]

    if (baseline === other) {
      return null
    }

    return {
      label: baseline
        ? `${Math.abs(Math.round(((other - baseline) / baseline) * 100))}%`
        : 'New',
      leftIsHigher: baseline > other,
    }
  }

  const tooltip = (campaign: SentimentCampaign, isLeft: boolean) => (
    <div
      className={cn(
        styles.tooltip,
        isLeft ? styles.tooltipLeft : styles.tooltipRight
      )}
    >
      <span className={styles.tooltipLabel}>{COPY.totalMentions}</span>
      <span className={styles.tooltipTotal}>{totalOf(campaign)}</span>

      <div className={styles.tooltipRows}>
        {BANDS.slice()
          .reverse()
          .map(({ key, label }) => (
            <span className={styles.tooltipRow} key={key}>
              <span className={cn(styles.tooltipDot, styles[key])} />
              <span className={styles.tooltipBand}>
                {label} - {shareOf(campaign, key)}%
              </span>
              <span className={styles.tooltipValue}>{campaign[key]}</span>
            </span>
          ))}
      </div>
    </div>
  )

  const column = (campaign: SentimentCampaign, index: number) => {
    const total = totalOf(campaign)
    const isLeft = index === 0

    return (
      <div
        className={styles.column}
        onMouseEnter={() => setHovered(index)}
        onMouseLeave={() => setHovered(null)}
      >
        {hovered === index && tooltip(campaign, isLeft)}

        {/* No mentions is no bar. Drawn anyway it was three empty bands, each
            holding its own minimum height, hanging below the zero line and over
            the campaign's label. A band of zero is left out for the same
            reason. */}
        {total > 0 && (
          <div
            className={styles.stack}
            style={{ height: `${(total / axisTop) * 100}%` }}
          >
            {BANDS.filter(({ key }) => campaign[key] > 0).map(({ key }) => {
              // How tall this band actually lands, so the labels can be left
              // out of one too thin to hold them. The hover tooltip still
              // states every band's count, so nothing is lost with them.
              const bandHeight = (campaign[key] / axisTop) * PLOT_HEIGHT

              return (
                <div
                  className={cn(styles.band, styles[key])}
                  key={key}
                  style={{ flex: `${campaign[key]} 0 0` }}
                >
                  {bandHeight >= SHARE_FITS && (
                    <span className={styles.bandShare}>
                      {shareOf(campaign, key)}%
                    </span>
                  )}

                  {bandHeight >= VALUE_FITS && (
                    <span className={styles.bandValue}>{campaign[key]}</span>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
    )
  }

  // How this campaign stands against the other, for one band. Direction alone
  // decides the tone here: the card says which way it went, not whether it is
  // good news.
  const deltaCard = (move: BandMove | null, isThisSideHigher: boolean) => {
    // Nothing moved, so neither side is higher and there is nothing to claim.
    if (!move) {
      return <span className={styles.deltaEmpty} />
    }

    // 'New' belongs to the campaign that has the mentions; the side that has
    // none has no move of its own to state.
    if (move.label === 'New' && !isThisSideHigher) {
      return <span className={styles.deltaEmpty} />
    }

    return (
      <div
        className={cn(styles.delta, isThisSideHigher ? styles.up : styles.down)}
      >
        <span className={styles.deltaValue}>
          {isThisSideHigher ? '↑' : '↓'} {move.label}
        </span>
        <span className={styles.deltaLabel}>
          {isThisSideHigher ? COPY.higher : COPY.lower}
        </span>
      </div>
    )
  }

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <h3 className={styles.title}>{COPY.title}</h3>
        <p className={styles.subtitle}>{COPY.subtitle}</p>
      </div>

      {isEmpty ? (
        <p className={styles.empty}>{COPY.empty}</p>
      ) : (
        <>
          <div className={styles.chart}>
            {/* An axis on each side, as in the mock, so either bar can be read
            without crossing the whole chart. */}
            <div className={styles.axis}>
              {ticks.map((tick) => (
                <span className={styles.axisTick} key={tick}>
                  {tick}
                </span>
              ))}
            </div>

            <div className={styles.plot}>
              {/* The zero line the bars stand on, drawn across the whole plot. */}
              <span className={styles.baseline} />

              {/* A dashed line per axis tick, across the whole plot. The middle
              column is opaque and only as tall as its rows, so the lines run
              unbroken above it and stop either side of the cards. A repeating
              gradient cannot dash, so the lines are drawn. */}
              <div className={styles.grid}>
                {ticks.map((tick) => (
                  <span className={styles.gridLine} key={tick} />
                ))}
              </div>

              <div className={styles.side}>{column(left, 0)}</div>

              <div className={styles.middle}>
                {BANDS.map(({ key, label }) => {
                  const move = bandMove(key)

                  return (
                    <div className={styles.middleRow} key={key}>
                      {deltaCard(move, !!move && move.leftIsHigher)}

                      <div className={styles.bandSummary}>
                        <span
                          className={cn(styles.bandName, styles[`${key}Text`])}
                        >
                          {label}
                        </span>
                        <span className={styles.bandCounts}>
                          {left[key]} vs {right[key]}
                        </span>
                      </div>

                      {deltaCard(move, !!move && !move.leftIsHigher)}
                    </div>
                  )
                })}
              </div>

              <div className={styles.side}>{column(right, 1)}</div>
            </div>

            <div className={styles.axis}>
              {ticks.map((tick) => (
                <span className={styles.axisTick} key={tick}>
                  {tick}
                </span>
              ))}
            </div>
          </div>

          {/* A campaign's name is longer than the half-plot it sits under, so
              the label ellipsises and carries the whole name as its tooltip. */}
          <div className={styles.campaignLabels}>
            <TruncatedTooltip
              className={styles.campaignLabel}
              text={left.label}
            />
            <TruncatedTooltip
              className={styles.campaignLabel}
              text={right.label}
            />
          </div>
        </>
      )}

      {/* The bands the legend names are only drawn when there are any. */}
      {!isEmpty && (
        <div className={styles.legend}>
          {BANDS.slice()
            .reverse()
            .map(({ key, label }) => (
              <span className={styles.legendItem} key={key}>
                <span className={cn(styles.legendDot, styles[key])} />
                {label}
              </span>
            ))}
        </div>
      )}

      <div className={styles.insight}>
        <LightbulbIcon className={styles.insightIcon} />
        <p className={styles.insightText}>{insight}</p>
      </div>
    </div>
  )
}
